/**
 * Session page journey — from which pages users arrive / navigate before VIP checkout.
 *
 * First-touch path is frozen in sessionStorage so a checkout on `/` still knows
 * they entered via `/invitacion-premium` or `/bio/...` (or external referrer).
 *
 * Complements `detectTrafficSource` (channel: Instagram, UTM, ads) with **site paths**.
 */

const ENTRY_KEY = "aaf_session_entry_path";
const HISTORY_KEY = "aaf_session_path_history";
const MAX_HISTORY = 15;

export type SessionPageJourney = {
  /** First path seen in this tab session (e.g. `/invitacion-premium`). */
  entry_path: string;
  /** Query on first hit (utm_* kept; long tokens truncated). */
  entry_search: string;
  /** Path where tracking runs now. */
  current_path: string;
  current_search: string;
  /** Recent unique-ish path trail (oldest → newest, max MAX_HISTORY). */
  page_paths: string[];
  /** Full document.referrer if any. */
  referrer: string | null;
  referrer_domain: string | null;
};

function safePath(pathname: string | null | undefined): string {
  const p = (pathname || "/").trim() || "/";
  // Cap length for DB / Stripe metadata
  return p.slice(0, 200);
}

/** Keep utm_* / useful query; drop long tokens. */
function safeSearch(search: string | null | undefined): string {
  const raw = (search || "").trim();
  if (!raw || raw === "?") return "";
  try {
    const q = raw.startsWith("?") ? raw.slice(1) : raw;
    const params = new URLSearchParams(q);
    const keep = new URLSearchParams();
    for (const [k, v] of params.entries()) {
      const key = k.toLowerCase();
      if (
        key.startsWith("utm_") ||
        key === "gclid" ||
        key === "fbclid" ||
        key === "ttclid" ||
        key === "msclkid" ||
        key === "igshid" ||
        key === "ref" ||
        key === "source" ||
        key === "from" ||
        key === "lang" ||
        key === "country"
      ) {
        keep.set(k, v.slice(0, 120));
      }
    }
    const s = keep.toString();
    return s ? `?${s}`.slice(0, 300) : "";
  } catch {
    return raw.slice(0, 120);
  }
}

function readHistory(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === "string" && x.length > 0)
      .slice(-MAX_HISTORY);
  } catch {
    return [];
  }
}

/**
 * Call on every SPA route (PostHogPageview). Freezes entry_path on first hit.
 */
export function recordSessionPagePath(
  pathname?: string | null,
  search?: string | null,
): SessionPageJourney {
  const path =
    typeof pathname === "string"
      ? safePath(pathname)
      : typeof window !== "undefined"
        ? safePath(window.location.pathname)
        : "/";
  const q =
    typeof search === "string"
      ? safeSearch(search)
      : typeof window !== "undefined"
        ? safeSearch(window.location.search)
        : "";

  if (typeof window !== "undefined") {
    try {
      if (!sessionStorage.getItem(ENTRY_KEY)) {
        sessionStorage.setItem(
          ENTRY_KEY,
          JSON.stringify({ path, search: q, ts: Date.now() }),
        );
      }
      const hist = readHistory();
      const last = hist[hist.length - 1];
      if (last !== path) {
        hist.push(path);
        sessionStorage.setItem(
          HISTORY_KEY,
          JSON.stringify(hist.slice(-MAX_HISTORY)),
        );
      }
    } catch {
      /* private mode / blocked */
    }
  }

  return getSessionPageJourney(path, q);
}

export function getSessionPageJourney(
  currentPathOverride?: string,
  currentSearchOverride?: string,
): SessionPageJourney {
  const current_path =
    currentPathOverride ||
    (typeof window !== "undefined"
      ? safePath(window.location.pathname)
      : "/");
  const current_search =
    currentSearchOverride ??
    (typeof window !== "undefined" ? safeSearch(window.location.search) : "");

  let entry_path = current_path;
  let entry_search = current_search;
  if (typeof window !== "undefined") {
    try {
      const raw = sessionStorage.getItem(ENTRY_KEY);
      if (raw) {
        const o = JSON.parse(raw) as { path?: string; search?: string };
        if (o.path) entry_path = safePath(o.path);
        if (typeof o.search === "string") entry_search = safeSearch(o.search);
      } else {
        // First call without prior record — freeze now (no recurse via record*)
        sessionStorage.setItem(
          ENTRY_KEY,
          JSON.stringify({
            path: current_path,
            search: current_search,
            ts: Date.now(),
          }),
        );
        sessionStorage.setItem(HISTORY_KEY, JSON.stringify([current_path]));
      }
    } catch {
      /* ignore */
    }
  }

  let referrer: string | null = null;
  let referrer_domain: string | null = null;
  if (typeof document !== "undefined" && document.referrer) {
    referrer = document.referrer.slice(0, 500);
    try {
      referrer_domain = new URL(document.referrer).hostname
        .replace(/^www\./, "")
        .toLowerCase()
        .slice(0, 120);
    } catch {
      referrer_domain = null;
    }
  }

  return {
    entry_path,
    entry_search,
    current_path,
    current_search,
    page_paths: readHistory().length ? readHistory() : [current_path],
    referrer,
    referrer_domain,
  };
}

/**
 * Flat props for funnel events / PostHog / Slack (via mirrorFunnelEvent).
 */
export function pageJourneyFunnelProps(): Record<string, unknown> {
  const j = getSessionPageJourney();
  const out: Record<string, unknown> = {
    entry_path: j.entry_path,
    landing_path: j.entry_path,
    current_path: j.current_path,
    page_paths: j.page_paths.join(" › ").slice(0, 500),
    page_path_count: j.page_paths.length,
  };
  if (j.entry_search) out.entry_search = j.entry_search;
  if (j.current_search) out.current_search = j.current_search;
  if (j.referrer) out.referrer = j.referrer;
  if (j.referrer_domain) out.referrer_domain = j.referrer_domain;
  // Did they land elsewhere then convert on DualCTA home?
  out.landed_elsewhere =
    j.entry_path !== j.current_path && j.entry_path !== "/";
  return out;
}

/**
 * Stripe Checkout metadata (string values only, short keys).
 */
export function pageJourneyStripeMetadata(): Record<string, string> {
  const j = getSessionPageJourney();
  const out: Record<string, string> = {
    entry_path: j.entry_path.slice(0, 200),
    landing_path: j.entry_path.slice(0, 200),
    current_path: j.current_path.slice(0, 200),
  };
  if (j.page_paths.length > 1) {
    out.page_paths = j.page_paths.join(">").slice(0, 450);
  }
  if (j.referrer_domain) out.referrer_domain = j.referrer_domain.slice(0, 120);
  // Cap search hard: fbclid from IG routinely exceeds 200 and blows Stripe's 500 limit
  // when combined with other journey fields.
  if (j.entry_search) out.entry_search = j.entry_search.slice(0, 120);
  return out;
}

/**
 * Object for premium_checkout_intents.intent_journey JSONB.
 * Keep search strings short: Instagram `fbclid` alone can exceed Stripe's
 * 500-char metadata limit when stringified as intent_journey_json.
 */
export function pageJourneyIntentSnapshot(
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const j = getSessionPageJourney();
  const shortSearch = (s: string | undefined, max = 120) => {
    if (!s) return undefined;
    return s.length <= max ? s : `${s.slice(0, max)}…`;
  };
  return {
    entry_path: j.entry_path,
    entry_search: shortSearch(j.entry_search),
    current_path: j.current_path,
    current_search: shortSearch(j.current_search),
    page_paths: j.page_paths,
    referrer: j.referrer || undefined,
    referrer_domain: j.referrer_domain || undefined,
    captured_at: new Date().toISOString(),
    ...(extra && typeof extra === "object" ? extra : {}),
  };
}
